/**
 * NewOrderDialog - Dialog for creating a new order
 */
import React, { useState } from 'react';
import { useOrderStore } from '../../stores/orderStore';
import type { CustomerType, ServiceType } from '../../types';
import '../Dialogs/Dialog.css';

interface NewOrderDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NewOrderDialog: React.FC<NewOrderDialogProps> = ({ isOpen, onClose }) => {
  const { createOrder } = useOrderStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [customerType, setCustomerType] = useState<CustomerType>('individual');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [ico, setIco] = useState('');
  const [dic, setDic] = useState('');
  const [dicDph, setDicDph] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [latitude, setLatitude] = useState<number | undefined>();
  const [longitude, setLongitude] = useState<number | undefined>();
  const [serviceType, setServiceType] = useState<ServiceType>('internet');
  const [requestedSpeed, setRequestedSpeed] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await createOrder({
        customer_type: customerType,
        first_name: customerType === 'individual' ? firstName : undefined,
        last_name: customerType === 'individual' ? lastName : undefined,
        company_name: customerType === 'company' ? companyName : undefined,
        ico: customerType === 'company' ? ico : undefined,
        dic: customerType === 'company' ? dic : undefined,
        dic_dph: customerType === 'company' ? dicDph : undefined,
        address,
        city,
        zip_code: zipCode,
        email,
        phone,
        latitude,
        longitude,
        service_type: serviceType,
        requested_speed: requestedSpeed,
        customer_notes: customerNotes,
      });

      // Reset form and close
      resetForm();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Chyba pri vytváraní objednávky');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setCustomerType('individual');
    setFirstName('');
    setLastName('');
    setCompanyName('');
    setIco('');
    setDic('');
    setDicDph('');
    setAddress('');
    setCity('');
    setZipCode('');
    setEmail('');
    setPhone('');
    setLatitude(undefined);
    setLongitude(undefined);
    setServiceType('internet');
    setRequestedSpeed('');
    setCustomerNotes('');
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <div className="dialog-overlay" onClick={handleClose}>
      <div className="dialog-container" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>📝 Nová objednávka</h2>
          <button className="dialog-close" onClick={handleClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="dialog-content">
          {error && (
            <div className="error-message" style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '4px', color: '#c00' }}>
              {error}
            </div>
          )}

          {/* Customer Type */}
          <div className="form-group">
            <label>Typ zákazníka *</label>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="radio"
                  value="individual"
                  checked={customerType === 'individual'}
                  onChange={(e) => setCustomerType(e.target.value as CustomerType)}
                />
                Fyzická osoba
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="radio"
                  value="company"
                  checked={customerType === 'company'}
                  onChange={(e) => setCustomerType(e.target.value as CustomerType)}
                />
                Firma
              </label>
            </div>
          </div>

          {/* Individual fields */}
          {customerType === 'individual' && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label>Meno *</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Priezvisko *</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </div>
              </div>
            </>
          )}

          {/* Company fields */}
          {customerType === 'company' && (
            <>
              <div className="form-group">
                <label>Názov firmy *</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>IČO</label>
                  <input
                    type="text"
                    value={ico}
                    onChange={(e) => setIco(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>DIČ</label>
                  <input
                    type="text"
                    value={dic}
                    onChange={(e) => setDic(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>IČ DPH</label>
                  <input
                    type="text"
                    value={dicDph}
                    onChange={(e) => setDicDph(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}

          {/* Address */}
          <div className="form-group">
            <label>Adresa *</label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Ulica a číslo"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Mesto *</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>PSČ *</label>
              <input
                type="text"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                placeholder="010 01"
                required
              />
            </div>
          </div>

          {/* Contact */}
          <div className="form-row">
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Telefón *</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+421 901 234 567"
                required
              />
            </div>
          </div>

          {/* GPS Coordinates */}
          <div className="form-row">
            <div className="form-group">
              <label>GPS Šírka</label>
              <input
                type="number"
                step="0.000001"
                value={latitude || ''}
                onChange={(e) => setLatitude(e.target.value ? parseFloat(e.target.value) : undefined)}
                placeholder="49.123456"
              />
            </div>
            <div className="form-group">
              <label>GPS Dĺžka</label>
              <input
                type="number"
                step="0.000001"
                value={longitude || ''}
                onChange={(e) => setLongitude(e.target.value ? parseFloat(e.target.value) : undefined)}
                placeholder="18.123456"
              />
            </div>
          </div>

          {/* Service */}
          <div className="form-group">
            <label>Typ služby *</label>
            <select
              value={serviceType}
              onChange={(e) => setServiceType(e.target.value as ServiceType)}
              required
            >
              <option value="internet">Internet</option>
              <option value="internet_tv">Internet + TV</option>
              <option value="internet_tv_phone">Internet + TV + Telefón</option>
            </select>
          </div>

          <div className="form-group">
            <label>Požadovaná rýchlosť</label>
            <input
              type="text"
              value={requestedSpeed}
              onChange={(e) => setRequestedSpeed(e.target.value)}
              placeholder="napr. 500 Mbps"
            />
          </div>

          {/* Notes */}
          <div className="form-group">
            <label>Poznámky zákazníka</label>
            <textarea
              value={customerNotes}
              onChange={(e) => setCustomerNotes(e.target.value)}
              rows={3}
              placeholder="Dodatočné požiadavky alebo poznámky..."
            />
          </div>

          <div className="dialog-actions">
            <button type="button" onClick={handleClose} disabled={isSubmitting}>
              Zrušiť
            </button>
            <button type="submit" className="btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Vytváram...' : 'Vytvoriť objednávku'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

